import { Component, inject, input, computed, ChangeDetectionStrategy } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "@shared/shared.module";
import type { ITaxCardCardModel, ICardUserCardModel } from "@app/views-interfaces";
import { CardModalService } from "@shared/card-modal";
import { FormGroupDirective, ControlContainer } from "@angular/forms";
import type { IiFirmRole } from "@interfaces/index";
import { AssigneesPanel } from "../assignees-panel.class";
import { compareByFirstAndLastNames } from "@shared/index";

type GetRoleDataFunction = (roleId: string) => ICardUserCardModel[];

@Component({
    selector: "wk-assignees-tax-panel",
    templateUrl: "./assignees-tax-panel.component.html",
    styleUrl: "../assignees-panel.scss",
    host: { class: "wk-cm__card-panel__assignees" },
    imports: [TranslateModule, SharedModule, JumpstartComponentsModule, ReactiveFormsModule],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssigneesTaxPanelComponent extends AssigneesPanel {
    private readonly cardModalService = inject(CardModalService);

    public cardDataToShowInModal = input.required<ITaxCardCardModel>();

    protected readonly isPartnerOrManagerRoleDisabled = computed(() => {
        const editDisabled = !this.cardModalService.canEditAssignee();
        return (role: IiFirmRole) => {
            return (role.IsManager || role.IsPartner) && editDisabled;
        };
    });

    protected readonly useiFirmUserId = computed(() => {
        return this.cardModalService.useiFirmUserId();
    });

    readonly usersPerRole = computed(() => {
        const defaultUser: ICardUserCardModel = {
            id: null,
            iFirmUserId: null,
            label: "General.None",
            active: true,
        };
        const cardData = this.cardDataToShowInModal();
        const rolesWithUsers = Array.from(this.iFirmRolesService.rolesWithUsers()?.values() ?? []);
        const map = new Map<string, ICardUserCardModel[]>();
        if (!rolesWithUsers || !rolesWithUsers.length) return map;

        for (const role of rolesWithUsers) {
            const value = cardData?.statusData?.RoleUserMapping?.find((r) => r.role.Id === role.Id);
            if (!value) {
                map.set(role.Id, [defaultUser]);
                continue;
            }

            const users = Array.from(role.Users.values())
                .filter((u) => u.Active || u.Id === value.userId || u.IFirmUserId === value.userId)
                .sort(compareByFirstAndLastNames)
                .map((u) => ({
                    id: u.Id,
                    iFirmUserId: u.IFirmUserId,
                    label: u.FullName,
                    active: u.Active,
                })) as ICardUserCardModel[];
            users.unshift(defaultUser);

            map.set(role.Id, users as ICardUserCardModel[]);
        }

        return map;
    });
}
